import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getFeatureFlags } from "../shared/featureFlagService";
import { getUserInfo } from "./settingsSlice";
import type { AppDispatch, RootState } from "./types";

export type OneWebDomainFeatureFlags = Record<string, unknown>;

export interface DataServiceFeatureFlagsState {
    flags: OneWebDomainFeatureFlags;
    isLoading: boolean;
    isLoaded: boolean;
    error: string | null;
}

const initialState: DataServiceFeatureFlagsState = {
    flags: {},
    isLoading: false,
    isLoaded: false,
    error: null,
};

const ONE_WEB_DOMAIN_SPEC = "one-web-domain";

export const fetchDataServiceFeatureFlags = createAsyncThunk<
    OneWebDomainFeatureFlags,
    void,
    { state: RootState; dispatch: AppDispatch }
>(
    "dataServiceFeatureFlags/fetch",
    async (_, { dispatch, getState }): Promise<OneWebDomainFeatureFlags> => {
        const { accountId, userId } = getState().session;

        if (userId) {
            await dispatch(getUserInfo(userId));
        }

        const subjectId = (getState().setting as { userInfo?: { subjectId?: string } }).userInfo
            ?.subjectId;

        return getFeatureFlags(ONE_WEB_DOMAIN_SPEC, {
            avataxAccountId: accountId ? String(accountId) : undefined,
            subjectId: subjectId ?? undefined,
        });
    },
    {
        condition: (_, { getState }) => {
            const { isLoading, isLoaded } = getState().dataServiceFeatureFlags;
            return !isLoading && !isLoaded;
        },
    }
);

const dataServiceFeatureFlagsSlice = createSlice({
    name: "dataServiceFeatureFlags",
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(fetchDataServiceFeatureFlags.pending, state => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchDataServiceFeatureFlags.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isLoaded = true;
                state.flags = action.payload ?? {};
            })
            .addCase(fetchDataServiceFeatureFlags.rejected, (state, action) => {
                state.isLoading = false;
                state.isLoaded = true;
                state.error = action.error.message ?? "Failed to fetch feature flags";
            });
    },
});

const selectDataServiceFeatureFlagsState = (state: RootState): DataServiceFeatureFlagsState =>
    state.dataServiceFeatureFlags;

export const selectDataServiceFeatureFlags = (state: RootState): OneWebDomainFeatureFlags =>
    selectDataServiceFeatureFlagsState(state).flags;

const hasFlag = (flags: OneWebDomainFeatureFlags, key: string): boolean =>
    typeof flags === "object" && flags !== null && Object.prototype.hasOwnProperty.call(flags, key);

export const selectShowThemeSwitcher = (state: RootState): boolean => {
    const flags = selectDataServiceFeatureFlags(state);
    if (hasFlag(flags, "showThemeSwitcher")) {
        return (flags as { showThemeSwitcher: boolean }).showThemeSwitcher;
    }
    return false;
};

export const selectShouldShowWaffleMenu = (state: RootState): boolean => {
    const flags = selectDataServiceFeatureFlags(state);
    if (hasFlag(flags, "shouldShowWaffleMenu")) {
        return (flags as { shouldShowWaffleMenu: boolean }).shouldShowWaffleMenu;
    }
    return false;
};

export const selectShouldShowLocaleSwitcherAdminECM = (state: RootState): boolean => {
    const flags = selectDataServiceFeatureFlags(state);
    if (hasFlag(flags, "shouldShowLocaleSwitcherAdminECM")) {
        return (flags as { shouldShowLocaleSwitcherAdminECM: boolean })
            .shouldShowLocaleSwitcherAdminECM;
    }
    return false;
};

export const selectShouldUseSideNav = (state: RootState): boolean => {
    const flags = selectDataServiceFeatureFlags(state);
    if (hasFlag(flags, "shouldUseSideNav")) {
        return (flags as { shouldUseSideNav: boolean }).shouldUseSideNav;
    }
    return true;
};

export default dataServiceFeatureFlagsSlice.reducer;
